import React, { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { useUser } from "src/shared/context/UserContext";
import { RangePreset, getPresetRange } from "./utils/dateRange";

export interface DateFilterBarProps {
  preset: RangePreset;
  startDate: string;
  endDate: string;
  onRangeChange: (start: string, end: string, preset: RangePreset) => void;
  title?: string;
  className?: string;
  size?: "normal" | "compact";
}

/** Utility to get system local date in YYYY-MM-DD format (avoids ISO UTC timezone shifts) */
export const getSystemLocalDate = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const DateFilterBar: React.FC<DateFilterBarProps> = ({ preset, startDate, endDate, onRangeChange, title = "Filter Period", className = "", size = "normal" }) => {
  const { user } = useUser();

  // Always pulls today's date from local client system time
  const today = getSystemLocalDate(new Date());

  const userMinDate = user?.createdOn ? getSystemLocalDate(new Date(user.createdOn)) : "2020-01-01";

  const [localPreset, setLocalPreset] = useState<RangePreset>(preset);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  useEffect(() => {
    setLocalPreset(preset);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [preset, startDate, endDate]);

  const handlePresetChange = (value: RangePreset) => {
    setLocalPreset(value);
    const range = getPresetRange(value);
    if (range) {
      onRangeChange(range.startDate, range.endDate, value);
    } else if (value === "CustomizeDate") {
      const defaultRange = getPresetRange("LastMonth");
      if (defaultRange && preset !== "CustomizeDate") {
        setTempStartDate(defaultRange.startDate);
        setTempEndDate(defaultRange.endDate);
      }
    }
  };

  const handleApplyCustomDate = () => {
    if (tempStartDate && tempEndDate) {
      if (tempStartDate <= tempEndDate) {
        onRangeChange(tempStartDate, tempEndDate, "CustomizeDate");
      } else {
        alert("Start date cannot be after end date");
      }
    }
  };

  const isCustomDateChanged = localPreset === "CustomizeDate" && (tempStartDate !== startDate || tempEndDate !== endDate);

  const isCompact = size === "compact";

  return (
    <div className={`flex items-center justify-between bg-white p-2 sm:p-2 rounded-2xl shadow-sm border border-slate-200 flex-wrap gap-2 ${className}`}>
      {title && <span className={`${isCompact ? "text-base font-bold" : "text-base sm:text-lg font-bold"} text-gray-800 tracking-tight`}>{title}</span>}

      <div className="flex items-end gap-2 flex-wrap">
        {/* Preset Selector */}
        <div className="flex flex-col">
          <select
            value={localPreset}
            onChange={(e) => handlePresetChange(e.target.value as RangePreset)}
            className={`rounded-lg border border-gray-300 bg-white text-gray-700 font-medium shadow-xs hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer ${
              isCompact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm"
            }`}
          >
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="LastWeek">Last 7 days</option>
            <option value="LastMonth">Last 30 days</option>
            <option value="180days">Last 180 days</option>
            <option value="CustomizeDate">Customize date</option>
          </select>
        </div>

        {localPreset === "CustomizeDate" && (
          <>
            {/* From Date */}
            <div className="flex flex-col">
              <label className="text-[10px] font-medium text-gray-500 mb-0.5">From</label>
              <input
                type="date"
                value={tempStartDate}
                min={userMinDate}
                max={tempEndDate || today}
                onChange={(e) => setTempStartDate(e.target.value)}
                className={`rounded-lg border border-gray-300 bg-white text-gray-700 font-medium shadow-xs hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  isCompact ? "px-2 py-1.5 text-xs w-[130px]" : "px-3 py-2 text-sm w-[155px]"
                }`}
              />
            </div>

            {/* To Date */}
            <div className="flex flex-col">
              <label className="text-[10px] font-medium text-gray-500 mb-0.5">To</label>
              <input
                type="date"
                value={tempEndDate}
                min={tempStartDate || userMinDate}
                max={today}
                onChange={(e) => setTempEndDate(e.target.value)}
                className={`rounded-lg border border-gray-300 bg-white text-gray-700 font-medium shadow-xs hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  isCompact ? "px-2 py-1.5 text-xs w-[130px]" : "px-3 py-2 text-sm w-[155px]"
                }`}
              />
            </div>

            {/* Apply Button */}
            <button
              onClick={handleApplyCustomDate}
              disabled={!isCustomDateChanged}
              className={`rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-1.5 ${isCompact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"} ${
                isCustomDateChanged ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md active:scale-95 cursor-pointer" : "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
              }`}
            >
              <Check className={isCompact ? "w-3.5 h-3.5" : "w-4 h-4"} />
              Apply
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DateFilterBar;
